import { ITEM_SYSTEM_FIELDS } from '@cheqroom/api';
import { useCallback } from 'react';

import { mapItemFieldIdToFieldId } from './helper';
import { ItemFields, useFetchItemFieldsQuery } from './python.hooks';
import { FieldType } from './types';

export interface UseItemFieldsHook {
	isLoading: boolean;
	isLoadingItem: (itemId: string) => boolean;
	getOriginalFields: (itemId: string) => ItemFields | null;
}

/**
 * This function fetches all the system fields and custom fields for the items
 * Ideally we would use the properties coming from the item itself, but this will fetch them if they are not all available
 */
const useItemFields = (itemIds: string[]): UseItemFieldsHook => {
	// Here we fetch all possible system fields as well as the fields.* wildcard to fetch all fields for the items regardless of item type
	// While this is not the most efficient, it is plenty fast and simplifies the code for the forseeable future
	// status: not a system field, but ItemInfo.Status reads it — EquipmentTable in kit and spotcheck mode
	// merges these fields into the item. The mapper adds the availability fields status depends on.
	const fieldIds = [
		...ITEM_SYSTEM_FIELDS.map<FieldType>((field) => field.id as FieldType),
		'fields.*' as FieldType,
		'status' as const,
	].map(mapItemFieldIdToFieldId) as FieldType[];
	const isLoadFieldDataEnabled = itemIds.length > 0 && fieldIds.length > 0;

	const {
		data: itemFieldsData = [],
		isLoading,
		isRefetching,
	} = useFetchItemFieldsQuery(
		{
			itemIds: [...new Set(itemIds)],
			fieldIds,
		},
		{
			enabled: isLoadFieldDataEnabled,
			keepPreviousData: true,
		}
	);

	const isLoadingItem = useCallback(
		(itemId: string) => {
			if (
				itemIds.includes(itemId) &&
				isRefetching &&
				!itemFieldsData?.some((field: ItemFields) => field._id === itemId)
			)
				return true;

			return isLoading && itemFieldsData?.some((field: ItemFields) => field._id === itemId);
		},
		[itemFieldsData, isLoading, isRefetching, itemIds]
	);

	const getOriginalFields = useCallback(
		(itemId: string) => {
			return itemFieldsData?.find(({ _id }) => _id === itemId) || null;
		},
		[itemFieldsData]
	);

	return {
		// loading state in general for the hook
		isLoading: isLoadFieldDataEnabled && isLoading,
		// loading state for a specific item
		isLoadingItem,
		// get the original data for an item
		getOriginalFields,
	};
};

export default useItemFields;
